// packages/ui/src/primitives/flags/FlagCore.tsx
//
// A flag image without MUI or emotion: the one implementation of `@tourtask/ui`'s flags. The MUI apps reach it
// through `@tourtask/ui/components/Flag` and `FlagWithLabel`, which change nothing about how it looks; a hosted
// website (apps/sites ships no MUI or emotion, docs/website-builder/README.md D14) imports it from here, through
// FlagWithLabelCore. Nothing under `primitives/` imports MUI, emotion, Next.js or the rest of `@tourtask/ui`
// (ESLint-fenced in eslint.config.mjs).
//
// The image is react-country-flag's `svg` mode, drawn here without the package (docs/embed-size-and-speed.md,
// "ui-small"; FlagCore.test.ts holds react-country-flag 3.1.0 as the oracle): an `<img>` of
// `{cdnUrl}{country, lower case}.{cdnSuffix}` in its 1em box. Without a `source` that is react-country-flag's own
// default, the flag-icons files on jsDelivr, which the MUI apps load. A hosted website passes its self-hosted copy
// (README D19: its pages load files from their own origin or the static host only).
import type { CSSProperties } from 'react';

import { languageToCountryCode } from '@tourtask/utils/shared/flags/languageToCountryCode';

/** Which flag: a country's, or the one that stands for a language (`languageToCountryCode`). */
export type FlagSubject = { countryCode: string; languageCode?: never } | { languageCode: string; countryCode?: never };

/**
 * Where the flag SVGs are fetched from, in react-country-flag's terms: `cdnUrl` is a folder URL ending in `/`,
 * `cdnSuffix` the file extension. A flag's file is `{cdnUrl}{country code, lower case}.{cdnSuffix}`.
 */
export interface FlagSvgSource {
  cdnUrl: string;
  cdnSuffix: string;
}

/** How a flag's image is fetched. */
export interface FlagImageOptions {
  /** Omitted: react-country-flag's default, the flag-icons files on jsDelivr (what the MUI apps load). */
  source?: FlagSvgSource;
  /**
   * `'lazy'`: fetched only when the flag is about to show (one in a closed menu is not), and not preloaded by
   * React's server renderer, which otherwise puts a `<link rel="preload">` in the head for every `<img>`.
   */
  loading?: 'eager' | 'lazy';
}

export type FlagCoreProps = FlagSubject &
  FlagImageOptions & {
    style?: CSSProperties;
    disabled?: boolean;
    /** `''` where a label next to the flag names it (FlagWithLabelCore). Omitted: the `<img>` has no `alt`. */
    alt?: string;
  };

/** react-country-flag's default source: the flag-icons files on jsDelivr. */
const DEFAULT_FLAG_SOURCE: FlagSvgSource = { cdnUrl: 'https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/', cdnSuffix: 'svg' };

/** The box react-country-flag gives its SVG, so an empty slot takes exactly a flag's place. */
const FLAG_BOX_STYLE: CSSProperties = {
  display: 'inline-block',
  width: '1em',
  height: '1em',
  verticalAlign: 'middle',
};

export function FlagCore({ countryCode, languageCode, style, disabled, alt, source, loading }: FlagCoreProps) {
  const code = languageCode ? languageToCountryCode(languageCode) : countryCode;

  if (!code) {
    console.warn(`No flag country for "${languageCode ?? countryCode}"; rendering a blank slot.`);
    return <span aria-hidden="true" style={{ ...FLAG_BOX_STYLE, ...style }} />;
  }

  const finalStyle: CSSProperties = {
    ...style,
    filter: disabled ? 'grayscale(100%)' : undefined,
    opacity: disabled ? 0.6 : undefined,
  };

  // react-country-flag's `svg` branch, attribute for attribute: its other props first, then the source and the box
  // with the caller's style over it.
  const { cdnUrl, cdnSuffix } = source ?? DEFAULT_FLAG_SOURCE;
  return (
    <img
      alt={alt}
      loading={loading}
      src={`${cdnUrl}${code.toLowerCase()}.${cdnSuffix}`}
      style={{ ...FLAG_BOX_STYLE, ...finalStyle }}
    />
  );
}
